import React from "react";
import {
  BadgePercent,
  CircleCheckBig,
  Coins,
  Gift,
  History,
} from "lucide-react";
import useCtx from "../hooks/useCts";
import TopNavigator from "../components/TopNavigator";
import Footer from "../components/Footer";
import Skyline from "../assets/london-skyline.jpg";

const LoyaltyProgramScreen: React.FC = () => {
  const ctx = useCtx();

  const userClient = ctx.clients.find(
    (client) => client.authId === ctx.user.uid,
  );
  const school = ctx.schools.find((item) => item.id === userClient?.schoolId);

  const totalPoints =
    userClient?.points?.reduce((acc, point) => acc + point.value, 0) || 0;

  const rewardOptions = [
    {
      points: 50,
      reward: "Słodki upominek",
      type: "product",
    },
    {
      points: 100,
      reward: "Torba bawełniana 'Speak First, Think Later'",
      type: "product",
    },
    {
      points: 150,
      reward: "Torba bawełniana 'Speak First, Think Later' + słodki upominek",
      type: "product",
    },
    {
      points: 200,
      reward: "3 Edu Plakaty z Twoim logo",
      type: "product",
    },
    {
      points: 300,
      reward: "100 zł zniżki na kolejne zamówienie",
      type: "discount",
    },
    {
      points: 400,
      reward: "5 dowolnych podręczników gratis",
      type: "product",
    },
    {
      points: 700,
      reward: "10 dowolnych podręczników gratis",
      type: "product",
    },
    {
      points: 1000,
      reward: "400 zł zniżki na kolejne zamówienie",
      type: "discount",
    },
  ] as const;

  const formatDate = (timestamp: number) =>
    new Date(timestamp).toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <>
      <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#f7f2ea] text-gray-700">
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{ backgroundImage: `url(${Skyline})` }}
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[linear-gradient(180deg,rgba(255,250,245,0.82),rgba(255,255,255,0.66)_24%,rgba(248,250,252,0.5))]"
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_50%),radial-gradient(circle_at_88%_18%,rgba(44,150,233,0.24),transparent_54%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.24))]"
        />

        <div className="relative z-10">
          <TopNavigator />

          <main className="mx-auto w-full max-w-7xl px-4 pb-16 pt-6 sm:px-6 lg:px-8">
            {userClient ? (
              <>
                <section className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_360px] lg:items-start">
                  <div className="px-1 pt-4 sm:pt-8 lg:pt-12">
                    <h1 className="max-w-3xl bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text pb-1 text-3xl font-semibold leading-[1.12] tracking-[-0.04em] text-transparent sm:text-4xl lg:text-5xl">
                      Program lojalnościowy.
                    </h1>
                    <h2 className="mt-5 max-w-3xl font-medium leading-8 tracking-[-0.03em] text-slate-700 sm:text-2xl sm:leading-9 lg:text-[1.9rem]">
                      Śledź aktualne saldo, zobacz kolejne progi nagród i
                      odbierz benefity przy następnym zamówieniu.
                    </h2>
                  </div>

                  <aside className="w-full lg:max-w-[360px] lg:justify-self-end">
                    <div className="rounded-[28px] border border-white/50 bg-white/10 p-5 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm sm:p-6">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] border border-white/30 shadow-[0_14px_30px_rgba(148,163,184,0.36)]">
                          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-black/5 bg-white/50 text-slate-700">
                            <Coins
                              size={18}
                              strokeWidth={2.2}
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                            Twoje punkty
                          </p>
                          <p className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
                            Aktualne saldo
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 rounded-2xl border border-white/50 bg-white/10 p-4">
                        <p className="mt-2 text-4xl font-semibold tracking-[-0.04em] text-slate-900">
                          {totalPoints} pkt
                        </p>
                        <p className="mt-3 text-sm leading-6 text-slate-600">
                          Punkty są naliczane na Twoim koncie i możesz je
                          wykorzystać przy kolejnym zamówieniu.
                        </p>
                      </div>
                    </div>
                  </aside>
                </section>

                <section className="mt-10">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {rewardOptions.map((option) => {
                      const eligible = totalPoints >= option.points;
                      const missing = option.points - totalPoints;
                      const isDiscount = option.type === "discount";
                      const RewardIcon = isDiscount ? BadgePercent : Gift;

                      return (
                        <div
                          key={option.points}
                          className={`min-h-[280px] rounded-[28px] border p-5 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-md transition duration-300 ${
                            eligible
                              ? "border-white bg-white/80"
                              : "border-white/80 bg-white/62"
                          }`}
                        >
                          <div className="flex h-full flex-col">
                            <div className="flex items-center gap-4">
                              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[22px] border border-white/90 bg-white/88 shadow-[0_14px_30px_rgba(148,163,184,0.16)]">
                                <div
                                  className={`flex h-11 w-11 items-center justify-center rounded-2xl border border-black/5 shadow-sm ${
                                    isDiscount
                                      ? "bg-emerald-50 text-emerald-700"
                                      : "bg-orange-50 text-[#c55f0d]"
                                  }`}
                                >
                                  <RewardIcon
                                    className="h-5.5 w-5.5"
                                    strokeWidth={2.2}
                                    aria-hidden="true"
                                  />
                                </div>
                              </div>
                              <div className="min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                                  {isDiscount
                                    ? "Nagroda rabatowa"
                                    : "Nagroda rzeczowa"}
                                </p>
                                <p className="mt-2 text-xl font-semibold tracking-[-0.02em] text-slate-900">
                                  {option.points} pkt
                                </p>
                              </div>
                            </div>

                            <p className="mt-7 text-sm leading-7 text-slate-700">
                              {option.reward}
                            </p>

                            <div className="mt-auto pt-8">
                              {eligible ? (
                                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                                  <div className="flex items-center gap-2 font-semibold">
                                    <CircleCheckBig
                                      size={16}
                                      strokeWidth={2.2}
                                      aria-hidden="true"
                                    />
                                    Nagroda jest już dostępna
                                  </div>
                                  <p className="mt-2 leading-6">
                                    Zadzwoń lub napisz do nas, a dołączymy ją do
                                    kolejnego zamówienia.
                                  </p>
                                </div>
                              ) : (
                                <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-600">
                                  <div className="font-semibold text-slate-800">
                                    Brakuje jeszcze {missing} pkt
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="mt-10">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                      <History size={22} strokeWidth={2.2} aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                        Historia punktów
                      </p>
                      <h2 className="mt-1 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-2xl font-semibold tracking-[-0.03em] text-transparent">
                        Ostatnie naliczenia i saldo
                      </h2>
                    </div>
                  </div>

                  {userClient.points && userClient.points.length > 0 ? (
                    <div className="overflow-hidden rounded-[28px] border border-white/85 bg-white/10 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-md">
                      <div className="hidden grid-cols-[180px_100px_minmax(0,1fr)] gap-4 border-b border-slate-200/80 px-6 py-4 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 md:grid">
                        <span>Data</span>
                        <span>Punkty</span>
                        <span>Typ</span>
                      </div>

                      {userClient.points.map((point, index) => (
                        <div
                          key={`${point.date}-${index}`}
                          className="border-t border-slate-200/70 px-5 py-4 first:border-t-0"
                        >
                          <div className="flex flex-col gap-2 md:grid md:grid-cols-[180px_100px_minmax(0,1fr)] md:items-center md:gap-4">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 md:hidden">
                                Data
                              </p>
                              <p className="text-sm text-slate-700">
                                {formatDate(point.date)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 md:hidden">
                                Punkty
                              </p>
                              <p className="text-sm font-semibold text-slate-900">
                                {point.value} pkt
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 md:hidden">
                                Typ
                              </p>
                              <p className="text-sm text-slate-700">
                                {point.type}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="border-t border-slate-200/80 bg-white/70 px-5 py-4">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm font-semibold text-slate-700">
                            Łączna liczba punktów
                          </span>
                          <span className="text-lg font-semibold text-slate-900">
                            {totalPoints} pkt
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-[28px] border border-white/85 bg-white/10 p-6 text-slate-600 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-md">
                      Brak historii punktów.
                    </div>
                  )}
                </section>
              </>
            ) : (
              <div className="pt-12">
                <div className="mx-auto max-w-2xl rounded-[28px] border border-white/85 bg-white/10 p-8 text-center shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-md">
                  <h1 className="text-2xl font-semibold tracking-[-0.03em] text-slate-900">
                    Nie znaleziono konta w systemie.
                  </h1>
                  <p className="mt-4 text-slate-600">
                    Zaloguj się ponownie lub skontaktuj się z nami, jeśli
                    problem nadal występuje.
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default LoyaltyProgramScreen;
